export const JOB_BOARD_CHANNEL_KEYS = ['wttj', 'hellowork', 'apec', 'jobteaser', 'linkedin', 'indeed', 'francetravail'] as const
export type TJobBoardChannelKey = (typeof JOB_BOARD_CHANNEL_KEYS)[number]

export type TJobBoardChannel = {
  key: TJobBoardChannelKey
  label: string
  /** The board reads the public feed of the offers ticked for it: the firm turns it on in the career site settings */
  hasFeed: boolean
  /** The board sends the applications it receives straight to BestConsultant (no redirection to the career site) */
  pushesApplications: boolean
  /** BestConsultant publishes the offers ticked for the board through the board's API, on the firm's account */
  postsByApi: boolean
}

/**
 * A board as the API describes it to a firm: `isReady` says whether the board is open to the firm
 * (config/integrations.json of the API: enabled, or pilot firm). Not ready: its setup is hidden and refused;
 * an application tagged with its `?source=` still lands on its site of the firm's list.
 */
export type TJobBoardChannelReadiness = { isReady: boolean }

/**
 * Job boards an offer can be distributed to. Every board gets a tracked application link (`?source=<key>`),
 * so that a manual posting (LinkedIn...) also tells where the candidate comes from.
 */
export const JOB_BOARD_CHANNELS: TJobBoardChannel[] = [
  { key: 'wttj', label: 'Welcome to the Jungle', hasFeed: true, pushesApplications: false, postsByApi: false },
  { key: 'hellowork', label: 'HelloWork', hasFeed: true, pushesApplications: true, postsByApi: false },
  { key: 'jobteaser', label: 'JobTeaser', hasFeed: false, pushesApplications: false, postsByApi: true },
  { key: 'apec', label: 'APEC', hasFeed: false, pushesApplications: false, postsByApi: false },
  { key: 'linkedin', label: 'LinkedIn', hasFeed: false, pushesApplications: false, postsByApi: false },
  { key: 'indeed', label: 'Indeed', hasFeed: false, pushesApplications: false, postsByApi: false },
  { key: 'francetravail', label: 'France Travail', hasFeed: false, pushesApplications: false, postsByApi: false },
]

/** Query parameter of the career site URLs that carries the channel */
export const CAREER_CHANNEL_QUERY = 'source'

const CHANNEL_ALIASES: Record<string, TJobBoardChannelKey> = {
  'welcometothejungle': 'wttj',
  'welcome-to-the-jungle': 'wttj',
  'welcome_to_the_jungle': 'wttj',
  'hellowork.com': 'hellowork',
  'linkedin.com': 'linkedin',
  'indeed.com': 'indeed',
  'indeed.fr': 'indeed',
  'apec.fr': 'apec',
  'france-travail': 'francetravail',
  'france_travail': 'francetravail',
  'pole-emploi': 'francetravail',
  'poleemploi': 'francetravail',
}

export const isJobBoardChannel = (value: unknown): value is TJobBoardChannelKey =>
  typeof value === 'string' && (JOB_BOARD_CHANNEL_KEYS as readonly string[]).includes(value)

/** Channel key of a `?source=` / `utm_source` value, null when it is not a known board */
export const normalizeJobBoardChannel = (value: unknown): TJobBoardChannelKey | null => {
  if (typeof value !== 'string') return null
  const cleaned = value.trim().toLowerCase()
  if (isJobBoardChannel(cleaned)) return cleaned
  return CHANNEL_ALIASES[cleaned] ?? null
}

export const getJobBoardChannel = (key: unknown): TJobBoardChannel | undefined =>
  JOB_BOARD_CHANNELS.find(channel => channel.key === key)

/** A board whose feed the firm can turn on */
export const isJobBoardFeedReady = (channel: (Pick<TJobBoardChannel, 'hasFeed'> & TJobBoardChannelReadiness) | null | undefined) =>
  Boolean(channel?.hasFeed && channel.isReady)

/** A board the firm can post to, through its feed or its API */
export const isJobBoardPostingReady = (channel: (Pick<TJobBoardChannel, 'hasFeed' | 'postsByApi'> & TJobBoardChannelReadiness) | null | undefined) =>
  Boolean(channel?.isReady && (channel.hasFeed || channel.postsByApi))

/** At least one board is open to the firm: the job board fields of the offers are useful */
export const hasReadyJobBoard = (channels: TJobBoardChannelReadiness[]) => channels.some(channel => channel.isReady)

export const getCareerOfferPath = (firmId: string, offerId: string) => `/recruitment/career/${firmId}/${offerId}`

/** Public URL of an offer that tags the applications with the channel */
export const getCareerOfferChannelUrl = (baseUrl: string, firmId: string, offerId: string, channel: TJobBoardChannelKey) =>
  `${baseUrl.replace(/\/+$/, '')}${getCareerOfferPath(firmId, offerId)}?${CAREER_CHANNEL_QUERY}=${channel}`

// Job board fields of an offer, mapped to each board's own referential when it is sent
export const CONTRACT_KINDS = ['permanent', 'fixedTerm', 'internship', 'apprenticeship', 'freelance', 'temporary'] as const
export type TContractKind = (typeof CONTRACT_KINDS)[number]

// First match wins: an apprenticeship or an internship is often also a fixed-term contract
const CONTRACT_KIND_PATTERNS: [RegExp, TContractKind][] = [
  [/\b(alternan\w*|apprenti\w*|professionnalisation|work[ -]?study)\b/, 'apprenticeship'],
  [/\b(stages?|stagiaires?|intern(ship)?s?|trainees?)\b/, 'internship'],
  [/\b(interim\w*|temporaire|temporary|temp)\b/, 'temporary'],
  [/\b(freelance|free-lance|independant|contractor|portage)\b/, 'freelance'],
  [/\b(cdd|fixed[ -]?term|duree determinee)\b/, 'fixedTerm'],
  [/\b(cdi|permanent|duree indeterminee)\b/, 'permanent'],
]

/** Job board contract kind matching the name of a contract type of the firm (Base RH), null when none does */
export const guessContractKind = (display: unknown): TContractKind | null => {
  if (typeof display !== 'string')
    return null
  const name = display.normalize('NFD').replace(/[\u0300-\u036F]/g, '').toLowerCase()
  return CONTRACT_KIND_PATTERNS.find(([pattern]) => pattern.test(name))?.[1] ?? null
}

export const WORKING_TIMES = ['fullTime', 'partTime'] as const
export type TWorkingTime = (typeof WORKING_TIMES)[number]

export const REMOTE_POLICIES = ['none', 'partial', 'full'] as const
export type TRemotePolicy = (typeof REMOTE_POLICIES)[number]

export const SALARY_PERIODS = ['year', 'month', 'day', 'hour'] as const
export type TSalaryPeriod = (typeof SALARY_PERIODS)[number]
